import React, { useEffect, useRef, useState } from "react";
import "./edit-profile.css";
import { useEditProfileForm } from "./edit-profile.functions";
import { DropdownField, SingleRangeSlider } from "../../components/forms";
import Button from "../../components/primitives/button";
import { US_STATES } from "../../domain/profile-options/states";
import { POLITICAL_AFFILIATION } from "../../domain/profile-options/political-affiliation";
import { RELIGIOUS_AFFILIATION } from "../../domain/profile-options/religious-affiliation";
import { GENDERS, MARITAL_STATUSES, SEXUAL_ORIENTATIONS } from "../../domain/profile-options/demographics";
import {
  PROFILE_EDITOR_PREVIEW_SIZE,
  clampImageOffsets,
  computeRenderedImageSize,
} from "../../components/profile-image-editor/profile-image-editor.functions";
import { ProfileAvatar } from "../../components/profile-display";

export default function EditProfile() {
  const PREVIEW_SIZE = PROFILE_EDITOR_PREVIEW_SIZE;
  const fileInputRef = useRef(null);
  const dragRef = useRef(null);
  const pendingLeaveActionRef = useRef(null);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [savedImageLoadFailed, setSavedImageLoadFailed] = useState(false);
  const [showLeavePrompt, setShowLeavePrompt] = useState(false);
  const [pendingDestination, setPendingDestination] = useState("");
  const [resolvingLeaveAction, setResolvingLeaveAction] = useState(false);
  const {
    loading,
    saving,
    error,
    success,
    uploading,
    emailInitial,
    formData,
    profilePictureUrl,
    hasPendingImage,
    pendingImageUrl,
    pendingImageDimensions,
    imageZoom,
    imageOffsetX,
    imageOffsetY,
    storyModeration,
    sociopoliticalFilters,
    sociopoliticalPublic,
    setSociopoliticalPublic,
    hasUnsavedChanges,
    interestOptions,
    sociopoliticalQuestions,
    handleChange,
    handleImageUpload,
    setImageZoom,
    setImageOffsetX,
    setImageOffsetY,
    handleApplyImageAdjustments,
    handleCancelImageAdjustments,
    handleInterestToggle,
    handleSociopoliticalChange,
    handleIrrelevantToggle,
    saveProfile,
    handleSubmit,
  } = useEditProfileForm();

  const hasEditablePreview = hasPendingImage && Boolean(pendingImageUrl);
  const showSavedImage = Boolean(profilePictureUrl) && !savedImageLoadFailed;
  const renderedSize = computeRenderedImageSize(pendingImageDimensions, imageZoom);


  useEffect(() => {
    setSavedImageLoadFailed(false);
  }, [profilePictureUrl]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handleAttemptLeave = (event) => {
      if (!hasUnsavedChanges) return;
      const detail = event?.detail || {};
      const proceed = detail?.proceed;
      if (typeof proceed !== "function") return;

      const destination = String(detail?.to || "").trim();
      if (destination && destination === window.location.pathname) return;

      event.preventDefault();
      pendingLeaveActionRef.current = proceed;
      setPendingDestination(destination);
      setShowLeavePrompt(true);
    };

    window.addEventListener("app:attempt-leave", handleAttemptLeave);
    return () => window.removeEventListener("app:attempt-leave", handleAttemptLeave);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    return () => {
      pendingLeaveActionRef.current = null;
    };
  }, []);

  const normalizeOffsets = (nextX, nextY, zoom = imageZoom) =>
    clampImageOffsets(pendingImageDimensions, nextX, nextY, zoom);

  const handleZoomChange = (nextZoom) => {
    setImageZoom(nextZoom);
    const { x, y } = normalizeOffsets(imageOffsetX, imageOffsetY, nextZoom);
    setImageOffsetX(x);
    setImageOffsetY(y);
  };

  const handleImagePointerDown = (event) => {
    if (!hasEditablePreview) return;
    setIsDraggingImage(false);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      baseX: imageOffsetX,
      baseY: imageOffsetY,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleImagePointerMove = (event) => {
    if (!dragRef.current || !hasEditablePreview) return;
    const deltaX = event.clientX - dragRef.current.startX;
    const deltaY = event.clientY - dragRef.current.startY;
    if (!isDraggingImage && (Math.abs(deltaX) > 0 || Math.abs(deltaY) > 0)) {
      setIsDraggingImage(true);
    }
    const { x, y } = normalizeOffsets(
      dragRef.current.baseX - deltaX,
      dragRef.current.baseY - deltaY
    );
    setImageOffsetX(x);
    setImageOffsetY(y);
  };

  const handleImagePointerEnd = (event) => {
    if (!dragRef.current) return;
    if (event.currentTarget.hasPointerCapture(dragRef.current.pointerId)) {
      event.currentTarget.releasePointerCapture(dragRef.current.pointerId);
    }
    setIsDraggingImage(false);
    dragRef.current = null;
  };

  const closeLeavePrompt = () => {
    if (resolvingLeaveAction) return;
    pendingLeaveActionRef.current = null;
    setPendingDestination("");
    setShowLeavePrompt(false);
  };

  const continueToPendingDestination = () => {
    const action = pendingLeaveActionRef.current;
    pendingLeaveActionRef.current = null;
    setPendingDestination("");
    setShowLeavePrompt(false);
    setResolvingLeaveAction(false);
    if (typeof action === "function") action();
  };

  const handleSaveAndContinue = async () => {
    setResolvingLeaveAction(true);
    const result = await saveProfile();
    if (result?.success) {
      continueToPendingDestination();
      return;
    }
    setResolvingLeaveAction(false);
  };

  const handleContinueWithoutSaving = () => {
    if (resolvingLeaveAction) return;
    continueToPendingDestination();
  };



  const destinationLabel =
    pendingDestination === "__sign_out__"
      ? "sign out"
      : pendingDestination
        ? `go to ${pendingDestination}`
        : "leave this screen";

  if (loading) {
    return <div className="ep-loading">Loading profile...</div>;
  }

  return (
    <div className="ep-page">
      <div className="ep-shell">
        <main className="ep-main">
          <form onSubmit={handleSubmit} className="ep-form">
            {/* Profile identity */}
            <section className="ep-card">
              <h3 className="ep-title">Profile Identity</h3>
              <div className="ep-identityLayout">
                <div className="ep-identityPhotoCol">
                  <div className="ep-profile-picture-wrap">
                    {hasEditablePreview ? (
                      <div
                        className="ep-profile-picture-editor"
                        onPointerDown={handleImagePointerDown}
                        onPointerMove={handleImagePointerMove}
                        onPointerUp={handleImagePointerEnd}
                        onPointerCancel={handleImagePointerEnd}
                        style={{
                          "--ep-image-offset-x": `${imageOffsetX}px`,
                          "--ep-image-offset-y": `${imageOffsetY}px`,
                          "--ep-crop-size": `${PREVIEW_SIZE}px`,
                          cursor: isDraggingImage ? "grabbing" : "default",
                        }}
                      >
                        <img
                          className="ep-profile-picture-editor-image"
                          src={pendingImageUrl}
                          alt="Profile preview"
                          draggable={false}
                          style={{
                            width: `${renderedSize.width}px`,
                            height: `${renderedSize.height}px`,
                          }}
                        />
                        <div className="ep-profile-picture-crop-guide" />
                      </div>
                    ) : showSavedImage ? (
                      <img
                        className="ep-profile-picture"
                        src={profilePictureUrl}
                        alt="Profile"
                        onError={() => setSavedImageLoadFailed(true)}
                      />
                    ) : (
                      <ProfileAvatar
                        user={formData}
                        name={formData.profileName || emailInitial}
                        className="ep-profile-picture-fallback"
                      />
                    )}
                  </div>

                  <div className="ep-photoControlPanel">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={handleImageUpload}
                      aria-describedby="ep-photo-help"
                      style={{ display: "none" }}
                    />
                    {!hasPendingImage && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="ep-upload-button"
                      >
                        Choose Photo
                      </Button>
                    )}
                    {hasPendingImage && (
                      <div className="ep-image-controls">
                        <label className="ep-control-label">Zoom</label>
                        <input
                          className="ep-control-range"
                          type="range"
                          min="1"
                          max="2.5"
                          step="0.01"
                          value={imageZoom}
                          onChange={(e) => handleZoomChange(Number(e.target.value))}
                        />

                        <div className="ep-control-instruction">
                          Drag to position your photo inside the frame.
                        </div>

                        <div className="ep-image-control-actions">
                          <Button
                            type="button"
                            variant="primary"
                            onClick={handleApplyImageAdjustments}
                            disabled={uploading}
                          >
                            {uploading ? "Uploading..." : "Apply Photo"}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancelImageAdjustments}
                            disabled={uploading}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                    <small id="ep-photo-help" className="ep-help">
                      JPG or PNG, up to 5&nbsp;MB. Larger files are rejected before upload.
                    </small>
                  </div>
                </div>

                <div className="ep-identityInfoCol">
                  {error && <div className="ep-alert ep-alert-error">{error}</div>}
                  {success && <div className="ep-alert ep-alert-success">Profile updated successfully!</div>}
                  <div className="ep-grid ep-grid-2">
                    <div className="ep-field">
                      <label className="ep-label">Alias</label>
                      <input
                        className="ep-input"
                        type="text"
                        name="profileName"
                        value={formData.profileName || ""}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="ep-field">
                      <label className="ep-label">Email *</label>
                      <input
                        className="ep-input"
                        type="email"
                        name="email"
                        value={formData.email || ""}
                        onChange={handleChange}
                        required
                      />
                    </div>


                    <div className="ep-field">
                      <label className="ep-label">Date of Birth *</label>
                      <input
                        className="ep-input"
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        disabled
                        title="Date of birth is set during signup and can't be changed."
                        aria-describedby="dob-locked-hint"
                      />
                      <small id="dob-locked-hint" className="ep-help">
                        Locked — set during signup.
                      </small>
                    </div>

                    <div className="ep-field">
                      <label className="ep-label">Ethnicity *</label>
                      <input
                        className="ep-input"
                        type="text"
                        name="ethnicity"
                        value={formData.ethnicity}
                        onChange={handleChange}
                        disabled
                        title="Ethnicity is set during signup and can't be changed."
                        aria-describedby="ethnicity-locked-hint"
                      />
                      <small id="ethnicity-locked-hint" className="ep-help">
                        Locked — set during signup.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Location */}
            <section className="ep-card">
              <h3 className="ep-title">Location</h3>
              <div className="ep-grid ep-grid-3">
                <div className="ep-field">
                  <label className="ep-label">Country *</label>
                  <input
                    className="ep-input"
                    type="text"
                    name="country"
                    value="United States"
                    onChange={handleChange}
                    readOnly
                  />
                </div>

                <div className="ep-field ep-field--dropdown">
                  <DropdownField
                    label="State/Region"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    options={US_STATES}
                    placeholder="Select State"
                    required
                  />
                </div>

                <div className="ep-field">
                  <label className="ep-label">City *</label>
                  <input
                    className="ep-input"
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </section>

            {/* Demographics */}
            <section className="ep-card">
              <h3 className="ep-title">Demographics</h3>
              <div className="ep-grid ep-grid-2">
                <div className="ep-field">
                  <label className="ep-label">Marital Status *</label>
                  <DropdownField
                    label=""
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleChange}
                    options={MARITAL_STATUSES}
                    placeholder="Select Marital Status"
                    required
                  />
                </div>

                <div className="ep-field">
                  <label className="ep-label">Gender *</label>
                  <DropdownField
                    label=""
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    options={GENDERS}
                    placeholder="Select Gender"
                    required
                  />
                </div>

                <div className="ep-field ep-field--dropdown">
                  <DropdownField
                    label="Sexual Orientation"
                    name="sexualOrientation"
                    value={formData.sexualOrientation}
                    onChange={handleChange}
                    options={SEXUAL_ORIENTATIONS}
                    placeholder="Select Sexual Orientation"
                    required
                  />
                </div>

                <div className="ep-field">
                  <label className="ep-label">Occupation</label>
                  <input
                    className="ep-input"
                    type="text"
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleChange}
                  />
                </div>

                <div className="ep-field ep-field--dropdown">
                  <DropdownField
                    label="Religious Affiliation"
                    name="religiousAffiliation"
                    value={formData.religiousAffiliation}
                    onChange={handleChange}
                    options={RELIGIOUS_AFFILIATION}
                    placeholder="Select Religious Affiliation"
                    required
                  />
                </div>

                <div className="ep-field ep-field--dropdown">
                  <DropdownField
                    label="Political Affiliation"
                    name="politicalAffiliation"
                    value={formData.politicalAffiliation}
                    onChange={handleChange}
                    options={POLITICAL_AFFILIATION}
                    placeholder="Select Political Affiliation"
                    required
                  />
                </div>
              </div>
            </section>

            {/* Sociopolitical views */}
            <section className="ep-card">
              <h3 className="ep-title">Sociopolitical Views</h3>
              <div className="ep-sliderStack">
                {sociopoliticalQuestions.map((question, index) => {
                  const row = sociopoliticalFilters?.[question.id];
                  const irrelevant = !!row?.irrelevant;
                  const value = row?.value ?? 3;

                  return (
                    <div className="ep-sliderCard" key={question.id}>
                      <span className="ep-sliderIndex" aria-hidden="true">{index + 1}</span>
                      <div className="ep-sliderInner">
                        <SingleRangeSlider
                          label={question.label}
                          leftLabel={question.leftLabel}
                          rightLabel={question.rightLabel}
                          value={value}
                          min={1}
                          max={7}
                          showOptOut={true}
                          showTicks={true}
                          onChange={(val) => handleSociopoliticalChange(question.id, val)}
                          irrelevant={irrelevant}
                          onIrrelevantToggle={() => handleIrrelevantToggle(question.id)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="ep-visibilityRow">
                <label className="ep-visibilityToggle" htmlFor="ep-sociopoliticalVisibility">
                  <span className="ep-visibilityText">
                    <span className="ep-visibilityLabel">
                      Show my answers on my profile
                    </span>
                    <span className="ep-visibilityHint">
                      {sociopoliticalPublic
                        ? "Public — other members can see your answers."
                        : "Private — others see “This member’s answers are private.”"}{" "}
                      You can change this anytime in Settings.
                    </span>
                  </span>
                  <input
                    id="ep-sociopoliticalVisibility"
                    className="ep-visibilityInput"
                    type="checkbox"
                    role="switch"
                    aria-checked={sociopoliticalPublic}
                    checked={sociopoliticalPublic}
                    onChange={() => setSociopoliticalPublic((prev) => !prev)}
                  />
                  <span className="ep-visibilityTrack" aria-hidden="true">
                    <span className="ep-visibilityThumb" />
                  </span>
                </label>
              </div>
            </section>

            {/* Story */}
            <section className="ep-card">
              <h3 className="ep-title">About You</h3>
              <div className="ep-field">
                <label className="ep-label">Your Story (in a few words) *</label>
                <textarea
                  className="ep-textarea"
                  name="story"
                  value={formData.story}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  placeholder="Tell us a bit about yourself..."
                />
                <small className="ep-help">{(formData.story || "").length}/500 characters</small>
                {storyModeration.blocked ? (
                  <small className="ep-help ep-help--warning">{storyModeration.feedback}</small>
                ) : null}
              </div>
            </section>

            {/* Interests */}
            <section className="ep-card ep-card-last">
              <h3 className="ep-title">Interests (Select at least one) *</h3>
              <div className="ep-interests">
                {interestOptions.map((interest) => {
                  const selected = formData.interests.includes(interest);
                  return (
                    <Button
                      key={interest}
                      type="button"
                      variant={selected ? "primary" : "outline"}
                      onClick={() => handleInterestToggle(interest)}
                      className="ep-pill"
                    >
                      {interest}
                    </Button>
                  );
                })}
              </div>
            </section>

            <div className="ep-save-wrap">
              <Button type="submit" variant="primary" disabled={saving} className="ep-save">
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </main>
      </div>
      {/* Leave confirmation */}
      {showLeavePrompt && (
        <div className="ep-leaveOverlay" role="presentation">
          <div
            className="ep-leaveDialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ep-leave-title"
            aria-describedby="ep-leave-description"
          >
            <h3 id="ep-leave-title" className="ep-leaveTitle">Save changes before leaving?</h3>
            <p id="ep-leave-description" className="ep-leaveDescription">
              You have unsaved profile updates. Save now before you {destinationLabel}?
            </p>
            <div className="ep-leaveActions">
              <Button
                type="button"
                variant="primary"
                onClick={handleSaveAndContinue}
                disabled={resolvingLeaveAction || saving}
              >
                {resolvingLeaveAction || saving ? "Saving..." : "Save & Continue"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleContinueWithoutSaving}
                disabled={resolvingLeaveAction}
              >
                Continue Without Saving
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={closeLeavePrompt}
                disabled={resolvingLeaveAction}
              >
                Stay Here
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
